// The monthly summary, rendered three ways from one component:
//   * org-wide on the HR dashboard, with a one-click CSV export
//   * per person on their HR record
//   * their own, on Timesheets
//
// The three differ only in which rows RLS returns, whether the export button is
// shown and how one row is laid out — deliberately, so the number a member sees
// for themselves is the same number HR sees in the export.
import { Fragment, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Badge, Button, Mono } from './primitives';
import { Card, Eyebrow, PeriodStepper } from './card';
import { LoadError } from './feedback';
import { MONTHS } from './people';
import { downloadCsv } from '../lib/csv';
import { fmtHms } from '../lib/contract';
import { monthRange, orgToday, orgDateKey } from '../lib/dates';
import {
  deltaToDate,
  loadSummaries,
  loadLeaveTypes,
  type LeaveTypeColumn,
  type MonthSummary,
} from '../lib/monthlySummary';
import type { OrgHours } from '../lib/orgHours';
import type { Workdays } from '../lib/workdays';

/** "September 2026", from a 'YYYY-MM-01' key. Fixed English, like every other
 *  date in the app (UI-CONVENTIONS §2) — it followed the browser's locale. */
const MONTH_LABEL = (from: string): string =>
  `${MONTHS[Number(from.slice(5, 7)) - 1]} ${from.slice(0, 4)}`;

const th: CSSProperties = {
  padding: 'var(--hf-space-2) var(--hf-space-3)',
  textAlign: 'right',
  fontSize: 'var(--hf-text-2xs)',
  fontWeight: 'var(--hf-weight-medium)',
  color: 'var(--hf-ink-3)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--hf-track-eyebrow)',
  borderBottom: '1px solid var(--hf-rule)',
  whiteSpace: 'nowrap',
};
const td: CSSProperties = {
  padding: 'var(--hf-space-2) var(--hf-space-3)',
  textAlign: 'right',
  borderBottom: '1px solid var(--hf-rule)',
  whiteSpace: 'nowrap',
};
// The name column stays put while the other two dozen scroll under it: once you
// scrolled to the figures payroll prices, the row no longer said whose they were
// (HRA-V3). That pin, its card-coloured fill and the edge the columns slide
// under were a private `stuck` style here; they are .hf-table-scroll's now
// (tokens.css), with the fade that says the table goes on to the right.

// U+2212, not a hyphen: "-173.5" read as a dash before a number (TS-08).
const signed = (n: number): string => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : '0');
// Behind is amber, ahead is sage, and level is neither: zero is never toned.
const deltaTone = (n: number): string | undefined =>
  n < 0 ? 'var(--hf-amber-text)' : n > 0 ? 'var(--hf-sage-text)' : undefined;
// A figure's style, only when it is toned. `{ color: undefined }` is not "no
// tone": spread over Mono's own, it unset its ink-2, and a "0" Credited h or
// Unpaid leave read brighter than every other figure in the row.
const toned = (color: string | undefined): CSSProperties | undefined =>
  color ? { color } : undefined;
// The words after a figure ("to date"), at its size and quieter than it.
const qualifier: CSSProperties = { fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' };

// What a person reads their own month as: did I do enough, against what, from
// which hours. The table's column order is HR's spreadsheet; this is the same
// columns grouped by the question they answer (TS-07).
const GROUPS = ['Result', 'Target', 'Worked', 'Leave', 'Overtime and flags'] as const;
type Group = (typeof GROUPS)[number];

// One place, so the table, the person card and the CSV can never disagree about
// what a column means. `csv` is what lands in the file; `cell` is what lands on
// screen, defaulting to the same value in tabular figures.
type Column = {
  key: string;
  label: string;
  csv: (r: MonthSummary) => string | number;
  cell?: (r: MonthSummary) => ReactNode;
  /** Text, not a figure: left-aligned, and left out of the person card. */
  wide?: boolean;
  group?: Group;
};

// Everything up to and including the shortfall. The per-leave-type columns are
// built from the org's actual leave_types and spliced in after this.
const COLUMNS_BEFORE_LEAVE: Column[] = [
  { key: 'name', label: 'Person', csv: (r) => r.name, wide: true },
  { key: 'dept', label: 'Department', csv: (r) => r.department ?? '', wide: true },
  {
    key: 'perDay',
    label: 'Contract h/day',
    group: 'Target',
    csv: (r) => r.perDay,
    cell: (r) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--hf-space-2)' }}>
        <Mono>{r.perDay}</Mono>
        {/* "unset" must be visually distinct from an explicit 8, or HR cannot see
            who they still have to set up. */}
        {r.usesOrgDay && <Badge tone="neutral">org day</Badge>}
      </span>
    ),
  },
  { key: 'workingDays', label: 'Working days', group: 'Target', csv: (r) => r.workingDays },
  { key: 'leaveDays', label: 'Paid leave', group: 'Leave', csv: (r) => r.leaveDays },
  {
    key: 'unpaidDays',
    label: 'Unpaid leave',
    group: 'Leave',
    csv: (r) => r.unpaidDays,
    // Unpaid leave does NOT reduce required hours, so it lands in shortfall and
    // is priced at shortfall ÷ required × gross. It costs money, and until now
    // this table said nothing about it.
    cell: (r) => (
      <Mono style={toned(r.unpaidDays ? 'var(--hf-amber-text)' : undefined)}>{r.unpaidDays}</Mono>
    ),
  },
  { key: 'expectedDays', label: 'Expected days', group: 'Target', csv: (r) => r.expectedDays },
  { key: 'required', label: 'Required h', group: 'Target', csv: (r) => r.requiredHours },
  {
    key: 'requiredToDate',
    label: 'Required to date',
    group: 'Target',
    csv: (r) => r.requiredToDate,
  },
  {
    key: 'worked',
    label: 'Worked h',
    group: 'Worked',
    // No cell: the figure on screen is the figure in the file. It was padded to
    // "9.00" here beside a "176" and a "173.5" in the same row (TS-08).
    csv: (r) => r.workedHours,
  },
  {
    key: 'workedHms',
    label: 'Worked (h:mm:ss)',
    group: 'Worked',
    csv: (r) => fmtHms(r.workedSeconds),
  },
  // R5. Credited hours are shown beside worked ones rather than folded into them:
  // the clock and a client meeting are different evidence, and someone reconciling
  // a shortfall needs to see which is which. `effective` is what the shortfall and
  // the percentage are actually measured against.
  {
    key: 'comp',
    label: 'Credited h',
    group: 'Worked',
    csv: (r) => r.compHours,
    // Zero is "0" here like every other column; it was a "·" that read as a
    // missing value rather than none (TS-08).
    cell: (r) => (
      <Mono style={toned(r.compHours ? 'var(--hf-sky-text)' : undefined)}>{r.compHours}</Mono>
    ),
  },
  {
    key: 'effective',
    label: 'Effective h',
    group: 'Worked',
    csv: (r) => r.effectiveHours,
  },
  {
    key: 'pct',
    label: '% complete',
    group: 'Result',
    csv: (r) => r.pct,
    cell: (r) => (
      <Mono style={toned(r.pct >= 100 ? 'var(--hf-sage-text)' : undefined)}>{r.pct}%</Mono>
    ),
  },
  { key: 'avg', label: 'Avg h/day', group: 'Worked', csv: (r) => r.avgPerDay },
  {
    key: 'delta',
    label: 'Shortfall / surplus',
    group: 'Result',
    // The file keeps the month's figure: it is what payroll prices.
    csv: (r) => r.deltaHours,
    // On screen, an OPEN month leads with where the person stands against what
    // was due by today. Against the whole month everyone is short until the last
    // day, in amber, which taught people to ignore amber (TS-09).
    cell: (r) => {
      const now = deltaToDate(r);
      if (now == null)
        return <Mono style={toned(deltaTone(r.deltaHours))}>{signed(r.deltaHours)}</Mono>;
      return (
        <span
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 'var(--hf-space-1)',
          }}
        >
          {/* The figure in mono, the words after it not: "for the month" in
              mono read as code (critique 2026-10-06, mono carrying prose). */}
          <span style={qualifier}>
            <Mono style={toned(deltaTone(now))}>{signed(now)}</Mono> to date
          </span>
          <span style={qualifier}>
            <Mono dim>{signed(r.deltaHours)}</Mono> for the month
          </span>
        </span>
      );
    },
  },
];

// One column per leave type the org actually has. This was four hardcoded
// columns — Casual, Sick, Annual, Unpaid — so an org with two types carried two
// columns of permanent zeros, and a type of its own never appeared at all.
// `leaveByType` was always keyed by the real leave_type_id; only the list was
// invented.
//
// Paid types only. Unpaid leave already has its column ("Unpaid leave", priced
// into the shortfall), so an org with one unpaid type read "Unpaid leave" and
// "Unpaid", the same figure under two names (critique 2026-10-06 #6). An
// unknown `paid` is paid, as leave_types.paid defaults to.
const leaveColumns = (types: LeaveTypeColumn[]): Column[] =>
  types
    .filter((t) => t.paid !== false)
    .map((t) => ({
      key: `leave:${t.id}`,
      label: t.label,
      group: 'Leave' as const,
      csv: (r: MonthSummary) => r.leaveByType[t.id] ?? 0,
    }));

const COLUMNS_AFTER_LEAVE: Column[] = [
  { key: 'half', label: 'Half-days', group: 'Leave', csv: (r) => r.halfDays },
  { key: 'ot', label: 'Overtime h', group: 'Overtime and flags', csv: (r) => r.overtimeHours },
  // The comp off Org's "Overtime → comp off" credited this month: a count, never
  // leave (20261022100000). It was "Comp-off credited", two columns from
  // "Credited h" (compensated hours): two credits, near one name (critique
  // 2026-10-05 #6), so it carries no "credited".
  {
    key: 'compoff',
    label: 'Comp off (days)',
    group: 'Overtime and flags',
    csv: (r) => r.compOffCredited,
  },
  { key: 'present', label: 'Days present', group: 'Worked', csv: (r) => r.daysPresent },
  {
    key: 'autoClosed',
    label: 'Auto-closed',
    group: 'Overtime and flags',
    csv: (r) => r.autoClosedDays,
    // Those hours came from the nightly sweeper capping a forgotten check-out, not
    // from a real check-out. A payroll figure built on them is a guess, so the
    // count is shown rather than folded silently into the total.
    cell: (r) =>
      r.autoClosedDays ? <Badge tone="danger">{r.autoClosedDays}</Badge> : <Mono>0</Mono>,
  },
];

// A figure is set in tabular mono unless its column says otherwise: half the
// columns had no `cell` and fell back to the body face, so one row mixed two
// fonts and nothing lined up down a column (TS-08).
const show = (c: Column, r: MonthSummary): ReactNode =>
  c.cell ? c.cell(r) : c.wide ? c.csv(r) || '—' : <Mono>{c.csv(r)}</Mono>;

export const MonthlySummary = ({
  tz,
  org,
  workdays,
  onlyProfileId,
  variant = onlyProfileId ? 'person' : 'table',
  exportable,
  onOpenPerson,
  rowAction,
  rowLead,
  selection,
  initialOffset,
  onOffset,
  pinned,
}: {
  tz: string;
  org: OrgHours;
  workdays: Workdays | null;
  /**
   * Show ONE person's row. Pass this on every personal surface.
   *
   * Omitting it does not mean "one person" — it means "every row RLS returns",
   * which is one row for a member and the whole org for anyone holding
   * people:full + timesheets:full. Leaving it off the Timesheets mount put the
   * full org table on each admin's personal screen. Scope on what the surface
   * means, never on what the viewer's role happens to permit.
   */
  onlyProfileId?: string;
  /**
   * 'table' is the org's sheet: one row per person, every column, scrolling
   * sideways under a fixed name. 'person' is the same columns for ONE row, as
   * grouped figures with nothing off-screen — the default with `onlyProfileId`,
   * where a 25-column table for a single row hid the shortfall to the right.
   */
  variant?: 'table' | 'person';
  exportable?: boolean;
  /** Makes each person's name a button that opens their record. */
  onOpenPerson?: (id: string) => void;
  /**
   * Per-row actions, rendered as the SECOND column — immediately after the
   * person's name.
   *
   * The amendment workspace needs a VISIBLE way to act on one person. It had
   * onOpenPerson — a bare click anywhere on the row, with `cursor: pointer` and
   * a line of grey text as the only hint — which nobody could be expected to
   * find, and which overloads one gesture with two meanings across surfaces
   * (navigate on the dashboard, credit hours in the workspace).
   */
  rowAction?: (row: MonthSummary) => ReactNode;
  /** A control ahead of the name, inside the pinned first cell: a row's tick
   *  box, so it comes first and stays in view while the table scrolls. */
  rowLead?: (row: MonthSummary) => ReactNode;
  /** What acts on the ticked rows (Select all, the count, the action), in the
   *  card's header: it floated on the page between two cards (critique #8). */
  selection?: ReactNode;
  /**
   * Which month to open on, as an offset from the current org month (0 = this
   * month, -1 = last). The stepper still works from there — this sets where it
   * starts, it does not pin it. Needed by the amendment workspace, which is
   * always about one specific closed month.
   */
  initialOffset?: number;
  /**
   * Told each time the stepper moves. A parent that refetches by remounting
   * the table keeps this and passes it back as `initialOffset`, or the remount
   * would reopen on the current month (the People dashboard's bulk credit).
   */
  onOffset?: (next: number) => void;
  /**
   * Hide the month stepper, so the table cannot leave `initialOffset`. For a
   * surface that is ABOUT one month — the amendment workspace — where an action
   * on a row is dated to that month regardless of which month is on screen.
   */
  pinned?: boolean;
}) => {
  const [offset, setOffset] = useState(initialOffset ?? 0);
  const step = (next: number) => {
    setOffset(next);
    onOffset?.(next);
  };
  // undefined while loading, null when the read failed. A failed read used to
  // resolve to [] and render "Nothing recorded for September" (TS-14).
  const [rows, setRows] = useState<MonthSummary[] | null>();
  const [types, setTypes] = useState<LeaveTypeColumn[]>([]);
  const [attempt, setAttempt] = useState(0);

  // Configured leave types drive the per-type columns. Loaded once: they change
  // when someone edits Org -> Leave types, not when the month stepper moves.
  useEffect(() => {
    let live = true;
    void loadLeaveTypes().then((t) => live && setTypes(t));
    return () => {
      live = false;
    };
  }, []);

  const COLUMNS = useMemo(
    () => [...COLUMNS_BEFORE_LEAVE, ...leaveColumns(types), ...COLUMNS_AFTER_LEAVE],
    [types],
  );

  const range = useMemo(() => monthRange(orgToday(tz), offset), [tz, offset]);

  useEffect(() => {
    let live = true;
    setRows(undefined);
    void loadSummaries({
      from: range.from,
      to: range.to,
      tz,
      org,
      workdays,
      today: orgDateKey(Date.now(), tz),
    }).then(
      (r) => live && setRows(r.failed ? null : r),
      () => live && setRows(null),
    );
    return () => {
      live = false;
    };
  }, [range.from, range.to, tz, org, workdays, attempt]);

  const shown = useMemo(
    () =>
      onlyProfileId ? (rows ?? []).filter((r) => r.profileId === onlyProfileId) : (rows ?? []),
    [rows, onlyProfileId],
  );

  const exportCsv = () => {
    downloadCsv(`hyprflow-hours-${range.from.slice(0, 7)}.csv`, [
      COLUMNS.map((c) => c.label),
      ...shown.map((r) => COLUMNS.map((c) => c.csv(r))),
    ]);
  };

  const month = MONTH_LABEL(range.from);
  const quiet: CSSProperties = {
    fontSize: 'var(--hf-text-sm)',
    color: 'var(--hf-ink-3)',
    padding: 'var(--hf-space-2) 0',
  };

  return (
    <Card
      // A pinned table has no stepper to say which month it is about.
      label={pinned ? `Monthly summary · ${month}` : 'Monthly summary'}
      right={
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 'var(--hf-space-2)',
          }}
        >
          {selection}
          {exportable && shown.length > 0 && (
            <Button variant="ghost" size="sm" icon="download" onClick={exportCsv}>
              Export CSV
            </Button>
          )}
          {!pinned && (
            <PeriodStepper
              label={month}
              labelWidth="7.5rem"
              unit="month"
              onPrev={() => step(offset - 1)}
              onNext={() => step(Math.min(0, offset + 1))}
              nextDisabled={offset >= 0}
            />
          )}
        </div>
      }
    >
      {rows === undefined ? (
        <div role="status" style={quiet}>
          Loading…
        </div>
      ) : rows === null ? (
        <LoadError what={`the summary for ${month}`} onRetry={() => setAttempt((n) => n + 1)} />
      ) : shown.length === 0 ? (
        <div style={quiet}>Nothing recorded for {month}.</div>
      ) : variant === 'person' ? (
        // Columns, not a grid: the groups run 2 to 7 lines, and in grid rows
        // the fifth wrapped under the two-line Result, leaving a hole the
        // height of Leave beside it. Columns stack each group under the last;
        // the margin takes back the space after the last group of a column.
        <div
          style={{
            columnWidth: 220,
            columnGap: 'var(--hf-space-5)',
            marginBottom: 'calc(-1 * var(--hf-space-5))',
          }}
        >
          {GROUPS.map((g) => (
            <section key={g} style={{ breakInside: 'avoid', paddingBottom: 'var(--hf-space-5)' }}>
              <Eyebrow as="h3" style={{ marginBottom: 'var(--hf-space-2)' }}>
                {g}
              </Eyebrow>
              <dl style={{ margin: 0 }}>
                {COLUMNS.filter((c) => c.group === g).map((c) => (
                  <div
                    key={c.key}
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 'var(--hf-space-3)',
                      padding: 'var(--hf-space-1) 0',
                      borderBottom: '1px solid var(--hf-rule)',
                      fontSize: 'var(--hf-text-sm)',
                    }}
                  >
                    <dt style={{ color: 'var(--hf-ink-2)' }}>{c.label}</dt>
                    <dd style={{ margin: 0, textAlign: 'right' }}>{show(c, shown[0])}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      ) : (
        // The table is wider than any sidebar layout, so it scrolls in its own
        // box — the page body must never scroll sideways. Focusable, so the
        // keyboard can scroll it too. More than five columns, compared down
        // the rows: it keeps them on a phone too (UI-CONVENTIONS §8).
        <div
          role="region"
          aria-label={`Monthly summary table, ${month}`}
          tabIndex={0}
          className="hf-table-scroll"
        >
          <table
            style={{
              // Separate, not collapsed: a collapsed table paints its rules on
              // the table, not the cell, so a sticky cell slid away from its own
              // bottom rule and every row's line stopped at the name column.
              borderCollapse: 'separate',
              borderSpacing: 0,
              width: '100%',
              fontSize: 'var(--hf-text-sm)',
            }}
          >
            <thead>
              <tr>
                {COLUMNS.map((c, i) => (
                  <Fragment key={c.key}>
                    <th style={{ ...th, textAlign: c.wide ? 'left' : 'right' }}>{c.label}</th>
                    {/* Second column, straight after the name. This table is wider
                        than any layout it sits in and scrolls in its own box, so a
                        right-most action is off-screen until you scroll to find it —
                        which is the same problem as having no button at all. */}
                    {rowAction && i === 0 && (
                      <th style={{ ...th, textAlign: 'left' }}>
                        <span className="hf-sr-only">Actions</span>
                      </th>
                    )}
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.profileId}>
                  {COLUMNS.map((c, i) => (
                    <Fragment key={c.key}>
                      <td style={{ ...td, textAlign: c.wide ? 'left' : 'right' }}>
                        {/* The whole row used to be the click target: a <tr
                            onClick> no keyboard could reach or press (HRA-05).
                            The name is the link, and says so. */}
                        {i === 0 && rowLead && (
                          <span
                            style={{
                              display: 'inline-flex',
                              verticalAlign: 'middle',
                              marginRight: 'var(--hf-space-3)',
                            }}
                          >
                            {rowLead(r)}
                          </span>
                        )}
                        {i === 0 && onOpenPerson ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconRight="chevronRight"
                            className="hf-hit"
                            title={`Open ${r.name}`}
                            onClick={() => onOpenPerson(r.profileId)}
                            style={{
                              margin: '0 calc(-1 * var(--hf-space-2))',
                              padding: 'var(--hf-space-1) var(--hf-space-2)',
                              color: 'var(--hf-ink)',
                            }}
                          >
                            {r.name}
                          </Button>
                        ) : (
                          show(c, r)
                        )}
                      </td>
                      {rowAction && i === 0 && (
                        <td style={{ ...td, textAlign: 'left' }}>{rowAction(r)}</td>
                      )}
                    </Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};
